/**
 * Protected route component for admin-only pages
 * Requires Clerk authentication and admin role
 */

import { useIsAdmin } from "@/lib/auth";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { Navigate } from "react-router-dom";
import { AdminRouteLoading } from "@/components/admin/AdminStates";
import { preloadClerkForAdmin } from "@/lib/preloadClerkForAdmin";
import { markGeoSimEligible, markPrivacyGateBypass } from "@/lib/geoSim";
import { useEffect } from "react";

type ProtectedAdminRouteProps = {
  children: React.ReactNode;
  title?: string; // Optional title for backwards compatibility
};

const DEV_BYPASS = import.meta.env.VITE_ADMIN_DEV_BYPASS === "true";

/**
 * Dev-only shell: no Clerk hooks (ClerkProvider may be absent without publishable key).
 */
function ProtectedAdminRouteBypass({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    markGeoSimEligible();
    markPrivacyGateBypass();
  }, []);
  return <>{children}</>;
}

/**
 * Production path: requires ClerkProvider + admin role.
 */
function ProtectedAdminRouteClerk({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    preloadClerkForAdmin();
  }, []);

  // Admin panel visit → mark geo-sim eligible + privacy-gate bypass on public site
  useEffect(() => {
    if (isSignedIn && isAdmin) {
      markGeoSimEligible();
      markPrivacyGateBypass();
    }
  }, [isSignedIn, isAdmin]);

  if (!getClerkPublishableKey()) {
    return (
      <div className="fixed inset-0 z-[100] flex min-h-svh flex-col items-center justify-center gap-3 bg-black p-6 text-center">
        <p className="text-lg font-semibold text-white/90">
          Admin: Clerk no configurado en el build
        </p>
        <p className="max-w-md text-sm text-white/50">
          Define{" "}
          <code className="rounded bg-white/10 px-1 text-white/70">
            VITE_CLERK_PUBLISHABLE_KEY
          </code>{" "}
          o{" "}
          <code className="rounded bg-white/10 px-1 text-white/70">
            NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY
          </code>{" "}
          y vuelve a desplegar.
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return <AdminRouteLoading label="Cargando panel…" />;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Usuario logueado SIN rol admin → portal del rol "user"
  if (!isAdmin) {
    return <Navigate to="/portal" replace />;
  }

  return <>{children}</>;
}

/**
 * Protects admin routes - requires Clerk authentication and admin role.
 * VITE_ADMIN_DEV_BYPASS=true skips Clerk entirely (local QA).
 */
export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  if (DEV_BYPASS) {
    return <ProtectedAdminRouteBypass>{children}</ProtectedAdminRouteBypass>;
  }
  return <ProtectedAdminRouteClerk>{children}</ProtectedAdminRouteClerk>;
}

// Default export for backwards compatibility
export default ProtectedAdminRoute;
